import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Select,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Statistic,
  Tabs
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  FilterOutlined,
  EyeOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useStore } from '../../stores/useStore';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

interface Annotation {
  annotation_id: string;
  chunk_id: string;
  question_id: string;
  answer_value: any;
  model_confidence: number;
  human_edited: boolean;
  chunk_text: string;
  media_title: string;
  media_type: string;
}

const MetadataExplorer: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    media_type: undefined,
    question_id: undefined,
    human_edited: undefined
  });
  const [form] = Form.useForm();

  const { currentMediaTypes, currentContentCategories } = useStore();

  useEffect(() => {
    loadAnnotations();
  }, [studyId, filters]);

  const loadAnnotations = async () => {
    setLoading(true);
    try {
      // Mock API call - replace with actual API
      const mockAnnotations: Annotation[] = [
        {
          annotation_id: '1',
          chunk_id: 'chunk_1',
          question_id: 'archetypes',
          answer_value: {
            archetypes: [
              { name: 'Mentor Father', confidence: 0.89, evidence: 'Guidance and wisdom themes' },
              { name: 'Protective Father', confidence: 0.76, evidence: 'Defensive behaviors' }
            ]
          },
          model_confidence: 0.89,
          human_edited: false,
          chunk_text: 'The father character provides guidance and wisdom to his children...',
          media_title: 'Modern Family Drama',
          media_type: 'script'
        },
        {
          annotation_id: '2',
          chunk_id: 'chunk_2',
          question_id: 'themes',
          answer_value: {
            themes: [
              { name: 'Family bonds', relevance: 0.92 },
              { name: 'Sacrifice', relevance: 0.78 }
            ]
          },
          model_confidence: 0.84,
          human_edited: true,
          chunk_text: 'In this emotional scene, the father sacrifices his career...',
          media_title: 'Family Values Movie',
          media_type: 'transcript'
        }
      ];

      setTimeout(() => {
        setAnnotations(mockAnnotations);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to load annotations:', error);
      setLoading(false);
    }
  };

  const handleEditAnnotation = (annotation: Annotation) => {
    setSelectedAnnotation(annotation);
    form.setFieldsValue({
      answer_value: JSON.stringify(annotation.answer_value, null, 2)
    });
    setEditModalVisible(true);
  };

  const handleSaveEdit = async (values: any) => {
    if (!selectedAnnotation) return;

    try {
      const updatedAnnotation = {
        ...selectedAnnotation,
        answer_value: JSON.parse(values.answer_value),
        human_edited: true
      };

      // Update in state
      setAnnotations(prev => 
        prev.map(ann => 
          ann.annotation_id === selectedAnnotation.annotation_id 
            ? updatedAnnotation 
            : ann
        )
      );

      setEditModalVisible(false);
      setSelectedAnnotation(null);
    } catch (error) {
      console.error('Failed to save annotation:', error);
    }
  };

  const columns = [
    {
      title: 'Media',
      dataIndex: 'media_title',
      key: 'media_title',
      width: 200,
      render: (title: string, record: Annotation) => (
        <div>
          <Text strong>{title}</Text>
          <br />
          <Tag color={record.media_type === 'script' ? 'blue' : 'green'}>
            {record.media_type.toUpperCase()}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Analysis Type',
      dataIndex: 'question_id',
      key: 'question_id',
      width: 120,
      render: (type: string) => (
        <Tag color="purple">{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Content Preview',
      dataIndex: 'chunk_text',
      key: 'chunk_text',
      ellipsis: true,
      render: (text: string) => (
        <Text>{text.substring(0, 100)}...</Text>
      ),
    },
    {
      title: 'Confidence',
      dataIndex: 'model_confidence',
      key: 'model_confidence',
      width: 100,
      render: (confidence: number) => (
        <Tag color={confidence > 0.8 ? 'green' : confidence > 0.6 ? 'orange' : 'red'}>
          {Math.round(confidence * 100)}%
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'human_edited',
      key: 'human_edited',
      width: 100,
      render: (edited: boolean) => (
        <Tag color={edited ? 'green' : 'default'} icon={edited ? <CheckCircleOutlined /> : null}>
          {edited ? 'Reviewed' : 'Auto'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Annotation) => (
        <Space size="small">
          <Button 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedAnnotation(record);
              // Show preview modal or expand row
            }}
          />
          <Button 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEditAnnotation(record)}
          />
        </Space>
      ),
    },
  ];

  const summary = {
    total: annotations.length,
    humanEdited: annotations.filter(a => a.human_edited).length,
    avgConfidence: annotations.length > 0 
      ? Math.round(annotations.reduce((sum, a) => sum + a.model_confidence, 0) / annotations.length * 100)
      : 0,
    mediaTypes: Array.from(new Set(annotations.map(a => a.media_type))).length
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Space>
        <Title level={2} style={{ marginTop: 16 }}>
          Metadata Explorer
        </Title>
        <Paragraph>
          Review and edit AI-generated annotations for Study: {studyId}
        </Paragraph>
      </div>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Annotations"
              value={summary.total}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Human Reviewed"
              value={summary.humanEdited}
              suffix={`/ ${summary.total}`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Avg Confidence"
              value={summary.avgConfidence}
              suffix="%"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Media Types"
              value={summary.mediaTypes}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by media type"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, media_type: value }))}
            >
              {currentMediaTypes.map(type => (
                <Select.Option key={type.value} value={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by content category"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, content_category: value }))}
            >
              {currentContentCategories.map(category => (
                <Select.Option key={category.value} value={category.value}>
                  {category.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by analysis type"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, question_id: value }))}
            >
              <Select.Option value="archetypes">Archetypes</Select.Option>
              <Select.Option value="themes">Themes</Select.Option>
              <Select.Option value="sentiment">Sentiment</Select.Option>
              <Select.Option value="representation">Representation</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Search
              placeholder="Search content..."
              onSearch={(value) => console.log('Search:', value)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Annotations Table */}
      <Card title="Annotations">
        <Table
          columns={columns}
          dataSource={annotations}
          rowKey="annotation_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} annotations`,
          }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Annotation"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveEdit}
        >
          {selectedAnnotation && (
            <div>
              <Paragraph>
                <Text strong>Content:</Text> {selectedAnnotation.chunk_text}
              </Paragraph>
              
              <Form.Item
                name="answer_value"
                label="Analysis Results (JSON)"
                rules={[
                  { required: true, message: 'Please enter valid JSON' },
                  {
                    validator: (_, value) => {
                      try {
                        JSON.parse(value);
                        return Promise.resolve();
                      } catch {
                        return Promise.reject('Invalid JSON format');
                      }
                    }
                  }
                ]}
              >
                <TextArea rows={10} />
              </Form.Item>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default MetadataExplorer; 